import React, { useEffect, useState } from "react";
import { MapPin, Loader2, Plus, X, Trash2, Pencil, Star } from "lucide-react";
import api from "../../services/api";

const EMPTY = { label: "Domicile", full_name: "", street: "", zip: "", city: "", country: "France", phone: "", is_default: false };

const AddressesSection = () => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/addresses").then(({ data }) => setAddresses(data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const openNew = () => { setForm(EMPTY); setEditId(null); setShowForm(true); };
  const openEdit = (a) => { setForm({ ...a }); setEditId(a.id); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editId) await api.put(`/addresses/${editId}`, form);
      else await api.post("/addresses", form);
      setShowForm(false); setLoading(true); load();
    } catch (e) { /* ignore */ } finally { setSaving(false); }
  };

  const del = async (id) => { await api.delete(`/addresses/${id}`); setLoading(true); load(); };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#FF3B3B]" size={32} /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-extrabold italic uppercase tracking-tight text-slate-50 text-2xl md:text-3xl flex items-center gap-2"><MapPin size={26} className="text-[#FF3B3B]" /> Mes adresses</h1>
        {!showForm && <button onClick={openNew} className="btn-orange rounded-lg px-4 py-2.5 font-bold flex items-center gap-2"><Plus size={18} /> Ajouter</button>}
      </div>

      {showForm && (
        <form onSubmit={save} className="bg-[#10131A] rounded-2xl border border-[#1E232D] p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-slate-50 text-lg">{editId ? "Modifier l'adresse" : "Nouvelle adresse"}</h2>
            <button type="button" onClick={() => setShowForm(false)} className="text-slate-500 hover:text-white"><X size={20} /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input name="label" value={form.label} onChange={onChange} placeholder="Libellé (Domicile...)" className="border-2 border-[#222735] rounded-lg px-3 py-2.5 outline-none focus:border-[#E01F1F]" />
            <input name="full_name" value={form.full_name} onChange={onChange} placeholder="Nom complet" required className="border-2 border-[#222735] rounded-lg px-3 py-2.5 outline-none focus:border-[#E01F1F]" />
            <input name="street" value={form.street} onChange={onChange} placeholder="Adresse (rue, n°)" required className="border-2 border-[#222735] rounded-lg px-3 py-2.5 outline-none focus:border-[#E01F1F] sm:col-span-2" />
            <input name="zip" value={form.zip} onChange={onChange} placeholder="Code postal" required className="border-2 border-[#222735] rounded-lg px-3 py-2.5 outline-none focus:border-[#E01F1F]" />
            <input name="city" value={form.city} onChange={onChange} placeholder="Ville" required className="border-2 border-[#222735] rounded-lg px-3 py-2.5 outline-none focus:border-[#E01F1F]" />
            <input name="phone" value={form.phone} onChange={onChange} placeholder="Téléphone" className="border-2 border-[#222735] rounded-lg px-3 py-2.5 outline-none focus:border-[#E01F1F]" />
            <label className="flex items-center gap-2 text-[14px] text-slate-300">
              <input type="checkbox" name="is_default" checked={form.is_default} onChange={onChange} className="w-4 h-4 accent-[#E01F1F]" /> Adresse par défaut
            </label>
          </div>
          <button type="submit" disabled={saving} className="btn-orange rounded-lg px-6 py-3 font-bold mt-4 flex items-center gap-2 disabled:opacity-70">
            {saving && <Loader2 className="animate-spin" size={18} />} Enregistrer
          </button>
        </form>
      )}

      {addresses.length === 0 && !showForm ? (
        <div className="bg-[#10131A] rounded-2xl border border-[#1E232D] p-12 text-center text-slate-400">
          <MapPin size={40} className="text-slate-500 mx-auto mb-3" /> Aucune adresse enregistrée.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {addresses.map((a) => (
            <div key={a.id} className="bg-[#10131A] rounded-2xl border border-[#1E232D] p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold text-slate-50 flex items-center gap-2">{a.label}{a.is_default && <span className="text-[#FF3B3B] flex items-center gap-1 text-[12px]"><Star size={13} className="fill-[#E01F1F]" /> Par défaut</span>}</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => openEdit(a)} className="text-slate-500 hover:text-white"><Pencil size={16} /></button>
                  <button onClick={() => del(a.id)} className="text-slate-500 hover:text-red-500"><Trash2 size={16} /></button>
                </div>
              </div>
              <div className="text-[14px] text-slate-300 leading-relaxed">
                {a.full_name}<br />{a.street}<br />{a.zip} {a.city}<br />{a.country}
                {a.phone && <><br />{a.phone}</>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddressesSection;
